import React, { FC, useState } from 'react';
import { Box, Divider, TextButton, Button, Loader } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import MobileFullScreenModalLayout, {
  type MobileFullScreenModalVariant,
} from '@components/MobileFullScreenModalLayout';

export type MobileFilterModalFooterPadding = 'default' | 'compact';

type Props = {
  title: string;
  content: React.ReactNode;
  dataHook?: string;
  onClear?: () => void;
  onApply?: () => void;
  onClose?: () => void;
  onBack?: () => void;
  selectedAmount?: number;
  /** @default 'legacy' */
  shellVariant?: MobileFullScreenModalVariant;
  /** @default 'default' */
  footerPadding?: MobileFilterModalFooterPadding;
};

const MobileFilterModalLayout: FC<Props> = ({
  title,
  content,
  onClear,
  onApply,
  dataHook,
  onClose,
  onBack,
  selectedAmount,
  shellVariant = 'legacy',
  footerPadding = 'default',
}) => {
  return (
    <MobileFullScreenModalLayout
      title={title}
      content={content as JSX.Element}
      dataHook={dataHook}
      onClose={onClose}
      onBack={onBack}
      variant={shellVariant}
      footer={
        <FilterModalFooter
          selectedAmount={selectedAmount}
          onClear={onClear}
          onApply={onApply}
          footerPadding={footerPadding}
        />
      }
    />
  );
};

const FilterModalFooter: FC<
  Pick<Props, 'selectedAmount' | 'onClear' | 'onApply' | 'footerPadding'>
> = ({ selectedAmount, onClear, onApply, footerPadding = 'default' }) => {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState(false);
  const isCompact = footerPadding === 'compact';

  return (
    // Compact (directory): full-width footer; default (legacy): unchanged layout.
    <Box direction="vertical" width={isCompact ? '100%' : undefined}>
      <Divider />
      <Box
        align="space-between"
        verticalAlign="middle"
        {...(isCompact
          ? { padding: '24px' }
          : {
              paddingTop: '33px',
              paddingLeft: '24px',
              paddingRight: '24px',
              paddingBottom: '33px',
            })}
      >
        {onClear ? (
          <TextButton
            underline="always"
            skin="dark"
            dataHook="clear-all-mobile-filter-button"
            onClick={() => {
              onClear();
            }}
          >
            {t('mobile-filter-clear-all-with-amount', { selectedAmount })}
          </TextButton>
        ) : isCompact ? (
          <Box />
        ) : (
          <span></span>
        )}
        {onApply && (
          <Button
            dataHook="apply-mobile-filter-button"
            onClick={() => {
              onApply();
              setInProgress(true);
            }}
            disabled={inProgress}
          >
            {inProgress ? <Loader size="tiny" /> : t('mobile-filter-apply')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MobileFilterModalLayout;
